import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiChevronRight, FiCreditCard, FiTruck, FiShield } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { placeOrder } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PK_CITIES } from '../data/regions';
import './CheckoutPage.css';

const SHIPPING_THRESHOLD = 2000;
const SHIPPING_COST = 150;
const picsum = (seed) => `https://picsum.photos/seed/${seed}/56/56`;

function Field({ label, type = 'text', placeholder, value, onChange, error, ...rest }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input
        type={type}
        className={`form-input${error ? ' error' : ''}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        {...rest}
      />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

const STEPS = [
  { id: 1, label: 'Address' },
  { id: 2, label: 'Payment' },
  { id: 3, label: 'Confirm' },
];

const EMPTY_ADDRESS = {
  fullName: '', phone: '', street: '', city: '', state: '', zipCode: '',
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCartItems } = useCart();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS, fullName: user?.name || '', phone: user?.phone || '' });
  const [addressErrors, setAddressErrors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [card, setCard] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [cardErrors, setCardErrors] = useState({});
  const [placing, setPlacing] = useState(false);

  const shipping = cartTotal >= SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const grandTotal = cartTotal + shipping;

  // ── Validation ────────────────────────────────────────────────────────────
  const validateAddress = () => {
    const errors = {};
    if (!address.fullName.trim()) errors.fullName = 'Full name is required';
    if (!/^\d{10}$/.test(address.phone.replace(/\D/g, ''))) errors.phone = 'Enter a valid 10-digit number (e.g. 3001234567)';
    if (!address.street.trim()) errors.street = 'Street address is required';
    if (!address.city) errors.city = 'City is required';
    if (!address.state.trim()) errors.state = 'Province is required';
    if (!/^\d{4,6}$/.test(address.zipCode.replace(/\s/g, ''))) errors.zipCode = 'Enter a valid postal code';
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCard = () => {
    if (paymentMethod !== 'card') return true;
    const errors = {};
    if (card.name.trim().length < 2) errors.name = 'Cardholder name is required';
    if (!/^\d{13,19}$/.test(card.number.replace(/\s/g, ''))) errors.number = 'Enter a valid card number';
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(card.expiry)) errors.expiry = 'Use MM/YY format';
    if (!/^\d{3,4}$/.test(card.cvv)) errors.cvv = 'Enter a valid CVV';
    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressNext = () => { if (validateAddress()) setStep(2); };
  const handlePaymentNext = () => { if (validateCard()) setStep(3); };

  const handlePlaceOrder = async () => {
    const demoItem = cartItems.find((i) => !/^[a-f\d]{24}$/i.test(i._id));
    if (demoItem) {
      toast.error(`"${demoItem.name.slice(0, 30)}" is a demo product and can't be ordered.`);
      return;
    }
    setPlacing(true);
    try {
      const items = cartItems.map((i) => ({ product: i._id, quantity: i.quantity }));
      const { data } = await placeOrder({
        items,
        shippingAddress: { ...address, phone: `+92 ${address.phone}`, country: 'Pakistan' },
        paymentMethod,
        shippingCost: shipping,
        discount: 0,
      });
      clearCartItems();
      navigate(`/order-success/${data.order._id}`, {
        state: { orderNumber: data.order.orderNumber, total: data.order.total, itemCount: cartItems.length },
        replace: true,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const setField = (field) => (e) => {
    setAddress((a) => ({ ...a, [field]: e.target.value }));
    if (addressErrors[field]) setAddressErrors((er) => ({ ...er, [field]: '' }));
  };

  const setCardField = (field) => (e) => {
    let val = e.target.value;
    if (field === 'number') val = val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
    if (field === 'expiry') {
      val = val.replace(/\D/g, '').slice(0, 4);
      if (val.length > 2) val = val.slice(0, 2) + '/' + val.slice(2);
    }
    if (field === 'cvv') val = val.replace(/\D/g, '').slice(0, 4);
    setCard((c) => ({ ...c, [field]: val }));
    if (cardErrors[field]) setCardErrors((er) => ({ ...er, [field]: '' }));
  };

  // ── Mini components ───────────────────────────────────────────────────────
  const OrderItemsPreview = () => (
    <div className="confirm-items">
      {cartItems.map((item) => {
        const price = item.salePrice ?? item.price;
        return (
          <div key={item._id} className="confirm-item">
            <img src={item.images?.[0] || picsum(item._id)} alt={item.name} className="confirm-item-img"
              onError={(e) => { e.target.src = picsum(item.name); }} />
            <div className="confirm-item-info">
              <p className="confirm-item-name">{item.name}</p>
              {item.vendor?.storeName && <span className="confirm-item-vendor">{item.vendor.storeName}</span>}
            </div>
            <div className="confirm-item-right">
              <span className="confirm-qty">×{item.quantity}</span>
              <span className="confirm-price">Rs. {(price * item.quantity).toFixed(0)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const TotalPanel = () => (
    <div className="confirm-totals">
      <div className="confirm-total-row"><span>Subtotal</span><span>Rs. {cartTotal.toFixed(0)}</span></div>
      <div className="confirm-total-row"><span>Shipping</span><span>{shipping === 0 ? <span className="text-success">FREE</span> : `Rs. ${SHIPPING_COST}`}</span></div>
      <div className="confirm-total-row confirm-grand-total"><span>Total</span><span>Rs. {grandTotal.toFixed(0)}</span></div>
    </div>
  );

  return (
    <div className="checkout-page">
      <div className="container checkout-container">
        {/* Step indicator */}
        <div className="step-indicator">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="step-wrap">
              <div className={`step-circle ${step > s.id ? 'done' : step === s.id ? 'active' : ''}`}>
                {step > s.id ? <FiCheck size={14} /> : s.id}
              </div>
              <span className={`step-label ${step === s.id ? 'active' : ''}`}>{s.label}</span>
              {idx < STEPS.length - 1 && <div className={`step-line ${step > s.id ? 'done' : ''}`} />}
            </div>
          ))}
        </div>

        <div className="checkout-layout">
          <div className="checkout-main">
            <AnimatePresence mode="wait">
              {/* ── STEP 1: Address ─────────────────────────────────────── */}
              {step === 1 && (
                <motion.div key="step1" className="card"
                  initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
                  <div className="card-header">
                    <h2 className="checkout-step-title"><FiTruck size={18} /> Shipping Address</h2>
                  </div>
                  <div className="card-body checkout-form">
                    <Field label="Full Name *" name="fullName" placeholder="Muhammad Ali"
                      value={address.fullName} onChange={setField('fullName')} error={addressErrors.fullName} />

                    {/* Phone with static +92 prefix */}
                    <div className="form-group">
                      <label className="form-label">Phone Number *</label>
                      <div className="phone-input-wrap">
                        <span className="phone-code-static">+92</span>
                        <input
                          type="tel"
                          className={`form-input phone-number-input${addressErrors.phone ? ' error' : ''}`}
                          placeholder="3001234567"
                          value={address.phone}
                          onChange={setField('phone')}
                        />
                      </div>
                      {addressErrors.phone && <span className="form-error">{addressErrors.phone}</span>}
                    </div>

                    <Field label="Street Address *" name="street" placeholder="House 12, Street 4, Block B"
                      value={address.street} onChange={setField('street')} error={addressErrors.street} />

                    <div className="form-row-2">
                      {/* City dropdown */}
                      <div className="form-group">
                        <label className="form-label">City *</label>
                        <select
                          className={`form-select${addressErrors.city ? ' error' : ''}`}
                          value={address.city}
                          onChange={setField('city')}
                        >
                          <option value="">Select City</option>
                          {PK_CITIES.map((city) => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                        {addressErrors.city && <span className="form-error">{addressErrors.city}</span>}
                      </div>

                      {/* Province */}
                      <Field label="Province *" name="state" placeholder="Punjab"
                        value={address.state} onChange={setField('state')} error={addressErrors.state} />
                    </div>

                    <div className="form-row-2">
                      <Field label="Postal Code *" name="zipCode" placeholder="75500"
                        value={address.zipCode} onChange={setField('zipCode')} error={addressErrors.zipCode} />
                      {/* Country — static */}
                      <div className="form-group">
                        <label className="form-label">Country</label>
                        <input className="form-input" value="Pakistan" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                      </div>
                    </div>

                    <button className="btn btn-primary btn-lg" onClick={handleAddressNext}>
                      Continue to Payment <FiChevronRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 2: Payment ─────────────────────────────────────── */}
              {step === 2 && (
                <motion.div key="step2" className="card"
                  initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
                  <div className="card-header">
                    <h2 className="checkout-step-title"><FiCreditCard size={18} /> Payment Method</h2>
                  </div>
                  <div className="card-body checkout-form">
                    <div className="payment-options">
                      {[
                        { value: 'card', label: 'Credit / Debit Card', icon: '💳', sub: 'Visa, Mastercard, UnionPay' },
                        { value: 'cod', label: 'Cash on Delivery', icon: '💵', sub: 'Pay when you receive' },
                      ].map((opt) => (
                        <label key={opt.value} className={`payment-option ${paymentMethod === opt.value ? 'selected' : ''}`}>
                          <input type="radio" name="paymentMethod" value={opt.value}
                            checked={paymentMethod === opt.value} onChange={(e) => setPaymentMethod(e.target.value)} />
                          <span className="pay-opt-icon">{opt.icon}</span>
                          <div className="pay-opt-text"><strong>{opt.label}</strong><span>{opt.sub}</span></div>
                          <div className={`pay-opt-radio ${paymentMethod === opt.value ? 'checked' : ''}`} />
                        </label>
                      ))}
                    </div>

                    <AnimatePresence>
                      {paymentMethod === 'card' && (
                        <motion.div className="card-fields"
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <div className="card-visual">
                            <div className="card-chip">●●● ●●● ●●●</div>
                            <div className="card-number-display">
                              {(card.number || '•••• •••• •••• ••••').padEnd(19, '•')}
                            </div>
                            <div className="card-bottom">
                              <span>{card.name || 'CARDHOLDER NAME'}</span>
                              <span>{card.expiry || 'MM/YY'}</span>
                            </div>
                          </div>
                          <Field label="Cardholder Name *" name="cardName" placeholder="Muhammad Ali"
                            value={card.name} onChange={setCardField('name')} error={cardErrors.name} />
                          <Field label="Card Number *" name="cardNumber" placeholder="1234 5678 9012 3456"
                            value={card.number} onChange={setCardField('number')} error={cardErrors.number} />
                          <div className="form-row-2">
                            <Field label="Expiry *" name="expiry" placeholder="MM/YY"
                              value={card.expiry} onChange={setCardField('expiry')} error={cardErrors.expiry} />
                            <Field label="CVV *" name="cvv" type="password" placeholder="•••"
                              value={card.cvv} onChange={setCardField('cvv')} error={cardErrors.cvv} maxLength={4} />
                          </div>
                          <p className="card-security-note">
                            <FiShield size={13} /> Your card details are encrypted and never stored.
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="step-nav">
                      <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
                      <button className="btn btn-primary btn-lg" onClick={handlePaymentNext}>
                        Review Order <FiChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 3: Confirm ─────────────────────────────────────── */}
              {step === 3 && (
                <motion.div key="step3" className="card"
                  initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
                  <div className="card-header">
                    <h2 className="checkout-step-title"><FiCheck size={18} /> Review Your Order</h2>
                  </div>
                  <div className="card-body confirm-body">
                    <section className="confirm-section">
                      <h3 className="confirm-section-title">Items</h3>
                      <OrderItemsPreview />
                      <TotalPanel />
                    </section>

                    <section className="confirm-section">
                      <h3 className="confirm-section-title">Shipping To</h3>
                      <div className="confirm-address">
                        <p><strong>{address.fullName}</strong></p>
                        <p>+92 {address.phone}</p>
                        <p>{address.street}</p>
                        <p>{address.city}, {address.state} {address.zipCode}</p>
                        <p>Pakistan</p>
                      </div>
                      <button className="confirm-edit-btn" onClick={() => setStep(1)}>Edit Address</button>
                    </section>

                    <section className="confirm-section">
                      <h3 className="confirm-section-title">Payment</h3>
                      <div className="confirm-payment">
                        <span>{paymentMethod === 'card' ? `💳 Card ending ••••${card.number.replace(/\s/g, '').slice(-4) || '****'}` : '💵 Cash on Delivery'}</span>
                      </div>
                      <button className="confirm-edit-btn" onClick={() => setStep(2)}>Edit Payment</button>
                    </section>

                    <div className="step-nav">
                      <button className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
                      <button className="btn btn-primary btn-lg" onClick={handlePlaceOrder} disabled={placing}>
                        {placing ? 'Placing Order…' : `Place Order · Rs. ${grandTotal.toFixed(0)}`}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Side summary */}
          <aside className="checkout-sidebar">
            <div className="card">
              <div className="card-header">
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                  Order ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})
                </h3>
              </div>
              <div className="card-body" style={{ padding: '1rem' }}>
                {cartItems.map((item) => {
                  const price = item.salePrice ?? item.price;
                  return (
                    <div key={item._id} className="sidebar-item-row">
                      <span className="sidebar-item-name">{item.name}</span>
                      <span className="sidebar-item-price">×{item.quantity} · Rs. {(price * item.quantity).toFixed(0)}</span>
                    </div>
                  );
                })}
                <div className="summary-divider" style={{ margin: '0.75rem 0' }} />
                <div className="summary-row" style={{ fontSize: '0.85rem' }}><span>Subtotal</span><span>Rs. {cartTotal.toFixed(0)}</span></div>
                <div className="summary-row" style={{ fontSize: '0.85rem' }}><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `Rs. ${SHIPPING_COST}`}</span></div>
                <div className="summary-divider" style={{ margin: '0.5rem 0' }} />
                <div className="summary-row" style={{ fontWeight: 800, fontSize: '1rem', fontFamily: 'Syne, sans-serif' }}>
                  <span>Total</span><span>Rs. {grandTotal.toFixed(0)}</span>
                </div>
              </div>
            </div>
            <div className="checkout-trust">
              <span><FiShield size={14} /> Secure &amp; Encrypted</span>
              <span><FiTruck size={14} /> Fast Delivery</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
